import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Image from 'react-bootstrap/Image';
import InputGroup from 'react-bootstrap/InputGroup';
import Badge from 'react-bootstrap/Badge';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt, FaPlane, FaStickyNote, FaExclamationTriangle, FaCreditCard } from 'react-icons/fa';
import UnifiedModal from '../UnifiedModal';
import { bookOffer } from '../../redux/booking/actions';
import LoginModal from '../LoginModal';
import RentalTermsModal from '../RentalTermsModal';
import { FILTERS } from '../../constants/default';
import './BookingModal.css';
import useAfterLogin from '../../hooks/useAfterLogin';
import { parseAsLocalDateTime } from '../../utils/dateUtils';
import { getCountryCodeForLocation } from '../../utils/rentalTerms';
import { trackEvent } from '../../utils/analytics';

const countries = [
  { value: 'AF', label: 'Afghanistan' },
  { value: 'AL', label: 'Albania' },
  { value: 'DZ', label: 'Algeria' },
  { value: 'AS', label: 'American Samoa' },
  { value: 'AD', label: 'Andorra' },
  { value: 'AO', label: 'Angola' },
  { value: 'AI', label: 'Anguilla' },
  { value: 'AQ', label: 'Antarctica' },
  { value: 'AG', label: 'Antigua and Barbuda' },
  { value: 'AR', label: 'Argentina' },
  { value: 'AM', label: 'Armenia' },
  { value: 'AW', label: 'Aruba' },
  { value: 'AU', label: 'Australia' },
  { value: 'AT', label: 'Austria' },
  { value: 'AZ', label: 'Azerbaijan' },
  { value: 'BS', label: 'Bahamas' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'GG', label: 'Bailiwick of Guernsey' },
  { value: 'JE', label: 'Bailiwick of Jersey' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'BB', label: 'Barbados' },
  { value: 'BY', label: 'Belarus' },
  { value: 'BE', label: 'Belgium' },
  { value: 'BZ', label: 'Belize' },
  { value: 'BJ', label: 'Benin' },
  { value: 'BM', label: 'Bermuda' },
  { value: 'BQ', label: 'Bonaire, Sint Eustatius and Saba' },
  { value: 'BA', label: 'Bosnia and Herzegovina' },
  { value: 'BW', label: 'Botswana' },
  { value: 'BV', label: 'Bouvet Island' },
  { value: 'BR', label: 'Brazil' },
  { value: 'IO', label: 'British Indian Ocean Territory' },
  { value: 'VG', label: 'British Virgin Islands' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'BF', label: 'Burkina Faso' },
  { value: 'KH', label: 'Cambodia' },
  { value: 'CM', label: 'Cameroon' },
  { value: 'CA', label: 'Canada' },
  { value: 'KY', label: 'Cayman Islands' },
  { value: 'CF', label: 'Central African Republic' },
  { value: 'CL', label: 'Chile' },
  { value: 'CN', label: 'China' },
  { value: 'CO', label: 'Colombia' },
  { value: 'DM', label: 'Commonwealth of Dominica' },
  { value: 'MP', label: 'Commonwealth of the Northern Mariana Islands' },
  { value: 'CK', label: 'Cook Islands' },
  { value: 'CR', label: 'Costa Rica' },
  { value: 'HR', label: 'Croatia' },
  { value: 'CW', label: 'Curaçao' },
  { value: 'CY', label: 'Cyprus' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'KP', label: 'Democratic People\'s Republic of Korea' },
  { value: 'ST', label: 'Democratic Republic of São Tomé and Príncipe' },
  { value: 'TL', label: 'Democratic Republic of Timor-Leste' },
  { value: 'CD', label: 'Democratic Republic of the Congo' },
  { value: 'DK', label: 'Denmark' },
  { value: 'YT', label: 'Department of Mayotte' },
  { value: 'DO', label: 'Dominican Republic' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'EG', label: 'Egypt' },
  { value: 'SV', label: 'El Salvador' },
  { value: 'EE', label: 'Estonia' },
  { value: 'SZ', label: 'Eswatini' },
  { value: 'FK', label: 'Falkland Islands' },
  { value: 'FO', label: 'Faroe Islands' },
  { value: 'ET', label: 'Federal Democratic Republic of Ethiopia' },
  { value: 'SO', label: 'Federal Republic of Somalia' },
  { value: 'FM', label: 'Federated States of Micronesia' },
  { value: 'KN', label: 'Federation of Saint Christopher and Nevis' },
  { value: 'FJ', label: 'Fiji' },
  { value: 'FI', label: 'Finland' },
  { value: 'FR', label: 'France' },
  { value: 'GF', label: 'French Guiana' },
  { value: 'PF', label: 'French Polynesia' },
  { value: 'GA', label: 'Gabon' },
  { value: 'GM', label: 'Gambia' },
  { value: 'GE', label: 'Georgia' },
  { value: 'DE', label: 'Germany' },
  { value: 'GH', label: 'Ghana' },
  { value: 'GI', label: 'Gibraltar' },
  { value: 'GR', label: 'Greece' },
  { value: 'GL', label: 'Greenland' },
  { value: 'GD', label: 'Grenada' },
  { value: 'GP', label: 'Guadeloupe' },
  { value: 'GU', label: 'Guam' },
  { value: 'GT', label: 'Guatemala' },
  { value: 'GY', label: 'Guyana' },
  { value: 'HM', label: 'Heard Island and McDonald Islands' },
  { value: 'HN', label: 'Honduras' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'HU', label: 'Hungary' },
  { value: 'IS', label: 'Iceland' },
  { value: 'PG', label: 'Independent State of Papua New Guinea' },
  { value: 'WS', label: 'Independent State of Samoa' },
  { value: 'KI', label: 'Independent and Sovereign Republic of Kiribati' },
  { value: 'IN', label: 'India' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'IQ', label: 'Iraq' },
  { value: 'IE', label: 'Ireland' },
  { value: 'IR', label: 'Islamic Republic of Iran' },
  { value: 'MR', label: 'Islamic Republic of Mauritania' },
  { value: 'IM', label: 'Isle of Man' },
  { value: 'IL', label: 'Israel' },
  { value: 'IT', label: 'Italy' },
  { value: 'CI', label: 'Ivory Coast' },
  { value: 'JM', label: 'Jamaica' },
  { value: 'JP', label: 'Japan' },
  { value: 'JO', label: 'Jordan' },
  { value: 'KZ', label: 'Kazakhstan' },
  { value: 'KE', label: 'Kenya' },
  { value: 'BT', label: 'Kingdom of Bhutan' },
  { value: 'LS', label: 'Kingdom of Lesotho' },
  { value: 'TO', label: 'Kingdom of Tonga' },
  { value: 'XK', label: 'Kosovo' },
  { value: 'KW', label: 'Kuwait' },
  { value: 'KG', label: 'Kyrgyzstan' },
  { value: 'RE', label: 'La Réunion' },
  { value: 'LA', label: 'Laos' },
  { value: 'LV', label: 'Latvia' },
  { value: 'LB', label: 'Lebanon' },
  { value: 'LY', label: 'Libya' },
  { value: 'LI', label: 'Liechtenstein' },
  { value: 'LT', label: 'Lithuania' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'MO', label: 'Macao Special Administrative Region of the People\'s Republic of China' },
  { value: 'MW', label: 'Malawi' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'ML', label: 'Mali' },
  { value: 'MT', label: 'Malta' },
  { value: 'MQ', label: 'Martinique' },
  { value: 'MU', label: 'Mauritius' },
  { value: 'MX', label: 'Mexico' },
  { value: 'MD', label: 'Moldova' },
  { value: 'MC', label: 'Monaco' },
  { value: 'MN', label: 'Mongolia' },
  { value: 'ME', label: 'Montenegro' },
  { value: 'MS', label: 'Montserrat' },
  { value: 'MA', label: 'Morocco' },
  { value: 'MZ', label: 'Mozambique' },
  { value: 'MM', label: 'Myanmar' },
  { value: 'NA', label: 'Namibia' },
  { value: 'BN', label: 'Nation of Brunei, Abode of Peace' },
  { value: 'NP', label: 'Nepal' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'NC', label: 'New Caledonia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'NI', label: 'Nicaragua' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'NU', label: 'Niue' },
  { value: 'NO', label: 'Norway' },
  { value: 'OM', label: 'Oman' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'PS', label: 'Palestine' },
  { value: 'PA', label: 'Panama' },
  { value: 'PY', label: 'Paraguay' },
  { value: 'PE', label: 'Peru' },
  { value: 'PH', label: 'Philippines' },
  { value: 'PN', label: 'Pitcairn Group of Islands' },
  { value: 'BO', label: 'Plurinational State of Bolivia' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'QA', label: 'Qatar' },
  { value: 'BI', label: 'Republic of Burundi' },
  { value: 'CV', label: 'Republic of Cabo Verde' },
  { value: 'TD', label: 'Republic of Chad' },
  { value: 'CU', label: 'Republic of Cuba' },
  { value: 'DJ', label: 'Republic of Djibouti' },
  { value: 'GQ', label: 'Republic of Equatorial Guinea' },
  { value: 'GN', label: 'Republic of Guinea' },
  { value: 'GW', label: 'Republic of Guinea-Bissau' },
  { value: 'HT', label: 'Republic of Haiti' },
  { value: 'LR', label: 'Republic of Liberia' },
  { value: 'MG', label: 'Republic of Madagascar' },
  { value: 'NR', label: 'Republic of Nauru' },
  { value: 'NE', label: 'Republic of Niger' },
  { value: 'MK', label: 'Republic of North Macedonia' },
  { value: 'PW', label: 'Republic of Palau' },
  { value: 'SL', label: 'Republic of Sierra Leone' },
  { value: 'SS', label: 'Republic of South Sudan' },
  { value: 'TJ', label: 'Republic of Tajikistan' },
  { value: 'VU', label: 'Republic of Vanuatu' },
  { value: 'CG', label: 'Republic of the Congo' },
  { value: 'MV', label: 'Republic of the Maldives' },
  { value: 'MH', label: 'Republic of the Marshall Islands' },
  { value: 'SD', label: 'Republic of the Sudan' },
  { value: 'RO', label: 'Romania' },
  { value: 'RU', label: 'Russia' },
  { value: 'RW', label: 'Rwanda' },
  { value: 'EH', label: 'Sahrawi Arab Democratic Republic' },
  { value: 'BL', label: 'Saint Barthelemy' },
  { value: 'SH', label: 'Saint Helena, Ascension and Tristan da Cunha' },
  { value: 'MF', label: 'Saint Martin' },
  { value: 'PM', label: 'Saint Pierre and Miquelon' },
  { value: 'VC', label: 'Saint Vincent and the Grenadines' },
  { value: 'SM', label: 'San Marino' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'SN', label: 'Senegal' },
  { value: 'RS', label: 'Serbia' },
  { value: 'SC', label: 'Seychelles' },
  { value: 'SG', label: 'Singapore' },
  { value: 'SX', label: 'Sint Maarten' },
  { value: 'SK', label: 'Slovakia' },
  { value: 'SI', label: 'Slovenia' },
  { value: 'SB', label: 'Solomon Islands' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'GS', label: 'South Georgia and the South Sandwich Islands' },
  { value: 'KR', label: 'South Korea' },
  { value: 'ES', label: 'Spain' },
  { value: 'LK', label: 'Sri Lanka' },
  { value: 'LC', label: 'St. Lucia' },
  { value: 'ER', label: 'State of Eritrea' },
  { value: 'SR', label: 'Suriname' },
  { value: 'SJ', label: 'Svalbard og Jan Mayen' },
  { value: 'SE', label: 'Sweden' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'SY', label: 'Syrian Arab Republic' },
  { value: 'TW', label: 'Taiwan' },
  { value: 'TZ', label: 'Tanzania' },
  { value: 'CX', label: 'Territory of Christmas Island' },
  { value: 'NF', label: 'Territory of Norfolk Island' },
  { value: 'CC', label: 'Territory of the Cocos (Keeling) Islands' },
  { value: 'TF', label: 'Territory of the French Southern and Antarctic Lands' },
  { value: 'WF', label: 'Territory of the Wallis and Futuna Islands' },
  { value: 'TH', label: 'Thailand' },
  { value: 'TG', label: 'Togolese Republic' },
  { value: 'TK', label: 'Tokelau' },
  { value: 'TT', label: 'Trinidad and Tobago' },
  { value: 'TN', label: 'Tunisia' },
  { value: 'TR', label: 'Turkey' },
  { value: 'TM', label: 'Turkmenistan' },
  { value: 'TC', label: 'Turks and Caicos Islands' },
  { value: 'TV', label: 'Tuvalu' },
  { value: 'US', label: 'USA' },
  { value: 'UG', label: 'Uganda' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'KM', label: 'Union of the Comoros' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'UM', label: 'United States Minor Outlying Islands' },
  { value: 'UY', label: 'Uruguay' },
  { value: 'UZ', label: 'Uzbekistan' },
  { value: 'VA', label: 'Vatican City State' },
  { value: 'VE', label: 'Venezuela' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'VI', label: 'Virgin Islands of the United States' },
  { value: 'YE', label: 'Yemen' },
  { value: 'ZM', label: 'Zambia' },
  { value: 'ZW', label: 'Zimbabwe' },
  { value: 'AX', label: 'Åland Islands' }
];

const acrissToCategory = (code) => {
  let found = false;
  if (code) {
    found = FILTERS.carGroup.items.find(item => {
      return (item.value.match(code[0]));
    });
  }

  if (found) {
    return found.title;
  } else {
    return "";
  }
};

const acrissToCarType = (code) => {
  let found = false;
  if (code) {
    found = FILTERS.carType.items.find(item => {
      return (item.value.match(code[1]));
    });
  }

  if (found) {
    return found.title;
  } else {
    return "";
  }
};

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push({ value: time, label: time });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

// Utility functions for input formatting and validation
const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  // Format as international number with spaces
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

const formatCardNumber = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 19);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

const formatExpiryDate = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const formatCvv = (value) => value.replace(/\D/g, '').slice(0, 4);

const addMonths = (date, months) => {
  const nextDate = new Date(date);
  nextDate.setMonth(nextDate.getMonth() + months);
  return nextDate;
};

const getCardExpiryDate = (value) => {
  if (!value) return null;

  const match = /^(\d{2})\/(\d{2})$/.exec(value);
  if (!match) return null;

  const month = Number(match[1]);
  const year = 2000 + Number(match[2]);

  if (month < 1 || month > 12) return null;

  return new Date(year, month, 0, 23, 59, 59, 999);
};

const isValidLuhnCardNumber = (value) => {
  const digits = value.replace(/\D/g, '');
  if (!/^\d+$/.test(digits) || digits.length < 13 || digits.length > 19) {
    return false;
  }

  let sum = 0;
  let shouldDouble = false;

  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let digit = Number(digits.charAt(i));

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

const validateEmailRealtime = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhoneRealtime = (phone) => {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
};

const validateLatinCharacters = (text) => {
  // Allow only Latin characters (a-z, A-Z), digits, spaces, and common punctuation
  const latinRegex = /^[a-zA-Z\d\s\-,.'()]+$/;
  return latinRegex.test(text);
};

const calculateAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const validateField = (name, value, offer) => {
  let isValid = false;
  let error = '';
  switch (name) {
    case 'email':
      isValid = !!value && validateEmailRealtime(value);
      error = !value ? 'required' : !validateEmailRealtime(value) ? 'invalidEmail' : '';
      break;
    case 'phone':
      isValid = !!value && validatePhoneRealtime(value);
      error = !value ? 'required' : !validatePhoneRealtime(value) ? 'invalidPhone' : '';
      break;
    case 'firstName':
    case 'lastName':
      isValid = !!value && value.length >= 2 && validateLatinCharacters(value);
      if (!value) error = 'required';
      else if (value.length < 2) error = 'nameTooShort';
      else if (!validateLatinCharacters(value)) error = 'latinCharactersOnly';
      break;
    case 'streetAddress':
    case 'city':
      isValid = !!value && validateLatinCharacters(value);
      if (!value) error = 'required';
      else if (!validateLatinCharacters(value)) error = 'latinCharactersOnly';
      break;
    case 'dob':
      if (!value) {
        isValid = false;
        error = 'required';
      } else {
        const age = calculateAge(value);
        if (age < (offer?.terms?.minAllowedAge || 18)) {
          isValid = false;
          error = { key: 'ageTooYoung', params: { minAge: offer?.terms?.minAllowedAge || 18 } };
        } else if (age > 100) {
          isValid = false;
          error = 'ageTooOld';
        } else {
          isValid = true;
        }
      }
      break;
    case 'country':
      isValid = !!value;
      error = !value ? 'required' : '';
      break;
    case 'cardNumber': {
      const digits = value ? value.replace(/\D/g, '') : '';
      const hasValidLength = digits.length >= 13 && digits.length <= 19;

      if (!value) {
        isValid = false;
        error = 'required';
        break;
      }

      if (!hasValidLength) {
        isValid = false;
        error = 'cardNumberLengthInvalid';
        break;
      }

      isValid = isValidLuhnCardNumber(digits);
      error = isValid ? '' : 'cardNumberInvalid';
      break;
    }
    case 'expiryDate': {
      const expiryPattern = /^(0[1-9]|1[0-2])\/\d{2}$/;
      if (!value) {
        isValid = false;
        error = 'required';
        break;
      }

      if (!expiryPattern.test(value)) {
        isValid = false;
        error = 'expiryDateInvalid';
        break;
      }

      const expiryDate = getCardExpiryDate(value);
      const now = new Date();
      const dropOffDate = offer?.doDate ? parseAsLocalDateTime(offer.doDate) : null;
      const minimumExpiryDate = dropOffDate ? addMonths(dropOffDate, 3) : null;

      if (!expiryDate || expiryDate <= now) {
        isValid = false;
        error = 'expiryDateExpired';
        break;
      }

      if (minimumExpiryDate && expiryDate < minimumExpiryDate) {
        isValid = false;
        error = 'expiryDateTooSoon';
        break;
      }

      isValid = true;
      error = '';
      break;
    }
    case 'cvv': {
      const cvvDigits = value ? value.replace(/\D/g, '') : '';
      isValid = cvvDigits.length >= 3 && cvvDigits.length <= 4;
      error = !value ? 'required' : !isValid ? 'cvvInvalid' : '';
      break;
    }
    case 'notes':
      isValid = value.length <= 300;
      error = value.length > 300 ? 'notesTooLong' : '';
      break;
    default:
      isValid = true; // optional fields
  }
  return { isValid, error };
};

const BookingModal = ({ show, onHide, offer, isAirport, dir, cid }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.user);
  const residency = useSelector(state => state.prefs.residency);
  const locations = useSelector(state => state.locations.data || []);
  const booking = useSelector(state => state.booking);

  const getLocationName = (code) => {
    if (!locations || !Array.isArray(locations)) return code;
    const loc = locations.find(l => String(l.locationCode) === String(code));
    return loc ? loc : null;
  };

  const isRTL = dir === 'rtl';
  const locale = i18n.language;

  const formatCurrencyValue = (value, currencyCode) => {
    if (value === null || value === undefined || typeof value === 'object' || !currencyCode) return '—';

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '—';

    return numericValue.toLocaleString(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });
  };

  const payment = offer?.payment || {};
  const displayCurrency = payment.renderCurrency;
  const totalAmount = payment.renderTotal ?? 0;
  const payNowAmount = payment.renderPayNow ?? 0;
  const payAtPickupAmount = Math.max(totalAmount - payNowAmount, 0);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    phone: '',
    streetAddress: '',
    city: '',
    country: residency || '',
    dob: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    flightNumber: '',
    landingTime: '',
    notes: ''
  });
  const [fieldTouched, setFieldTouched] = useState({});
  const [fieldValid, setFieldValid] = useState({});
  const [errors, setErrors] = useState({});
  const [showLogin, setShowLogin] = useState(false);
  const [showRentalTerms, setShowRentalTerms] = useState(false);
  const rentalTermsCountryCode = getCountryCodeForLocation(locations, offer?.puLocation);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationFailed, setValidationFailed] = useState(false);

  const afterLogin = useAfterLogin();

  useEffect(() => {
    if (booking.data) {
      setIsSubmitting(false);
      onHide();
    }
  }, [booking.data, onHide]);

  useEffect(() => {
    setIsSubmitting(Boolean(booking.loading));
  }, [booking.loading]);

  useEffect(() => {
    if (booking.error) {
      setIsSubmitting(false);
      setErrors(prev => ({ ...prev, submit: { key: 'bookingError', params: { error: booking.error } } }));
    }
  }, [booking.error]);

  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  useEffect(() => {
    const newFieldValid = {};
    Object.keys(formData).forEach(name => {
      const value = formData[name];
      const { isValid } = validateField(name, value, offer);
      newFieldValid[name] = isValid;
    });
    setFieldValid(newFieldValid);

    // Check if there are any invalid form controls and hide error indicator if none
    const hasInvalidInputs = document.querySelector('.form-control.is-invalid') !== null;
    if (!hasInvalidInputs && validationFailed) {
      setValidationFailed(false);
    }
  }, [formData, validationFailed, offer]);

  const handleChange = useCallback((e) => {
    console.log('handleChange called for field', e.target.name, 'with value', e.target.value);
    const { name, value } = e.target;
    let processedValue = value;

    // Format phone number
    if (name === 'phone') {
      processedValue = formatPhoneNumber(value);
    }

    if (name === 'cardNumber') {
      processedValue = formatCardNumber(value);
    }
    if (name === 'expiryDate') {
      processedValue = formatExpiryDate(value);
    }
    if (name === 'cvv') {
      processedValue = formatCvv(value);
    }

    // Update form data
    setFormData(prev => ({ ...prev, [name]: processedValue }));

    // Mark field as touched
    setFieldTouched(prev => ({ ...prev, [name]: true }));

    // Real-time validation
    const { isValid, error } = validateField(name, processedValue, offer);
    setFieldValid(prev => ({ ...prev, [name]: isValid }));

    // Set or clear errors for this field if touched
    console.log('Field', name, 'touched:', true, 'isValid:', isValid);
    setErrors(prev => ({ ...prev, [name]: error ? error : undefined }));
  }, [fieldTouched, offer]);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setFieldTouched(prev => ({ ...prev, [name]: true }));
  }, []);

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'streetAddress', 'city', 'country', 'dob', 'cardNumber', 'expiryDate', 'cvv'];

    // Check required fields and validations
    requiredFields.forEach(field => {
      const { isValid, error } = validateField(field, formData[field], offer);
      if (!isValid) {
        newErrors[field] = error;
      }
    });

    // Check notes
    const { isValid: notesValid, error: notesError } = validateField('notes', formData.notes, offer);
    if (!notesValid) {
      newErrors.notes = notesError;
    }

    setErrors(newErrors);
    console.log('Setting errors in validateForm:', newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    console.log('handleSubmit called');
    e.preventDefault();
    if (!user) {
      console.log('user not logged in');
      setShowLogin(true);
      return;
    }
    if (!validateForm()) {
      console.log('form invalid');
      setValidationFailed(true);
      trackEvent('Booking Validation Error', {
        'event_category': 'Conversion',
        'event_label': 'Form validation failed'
      });
      // Mark all fields as touched to show validation errors
      setFieldTouched({
        firstName: true,
        lastName: true,
        email: true,
        phone: true,
        streetAddress: true,
        city: true,
        country: true,
        dob: true,
        cardNumber: true,
        expiryDate: true,
        cvv: true,
        flightNumber: true,
        landingTime: true,
        notes: true
      });
      return;
    }
    setValidationFailed(false);
    setErrors(prev => {
      const nextErrors = { ...prev };
      delete nextErrors.submit;
      return nextErrors;
    });
    console.log('form valid, submitting');
    trackEvent('Booking Confirmation', {
      'event_category': 'Conversion',
      'event_label': offer?.model || 'Car'
    });
    setIsSubmitting(true);
    const streetAddress = formData.streetAddress.trim();
    const normalizeOfferDate = (value) => {
      if (!value) return value;

      const date = parseAsLocalDateTime(value);
      const pad = (number) => String(number).padStart(2, '0');

      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const payload = {
      offerId: offer.id,
      userId: user.id,
      ...(isAirport && { flightNumber: formData.flightNumber, landingTime: formData.landingTime }),
      notes: formData.notes,
      payment: {
        cardHolderName: `${formData.firstName} ${formData.lastName}`.trim(),
        cardNumber: formData.cardNumber.replace(/\s/g, ''),
        expiryDate: formData.expiryDate,
        cvv: formData.cvv
      },
      offer: {
        model: offer.model,
        group: offer.group,
        pickupLocation: offer.puLocation,
        dropoffLocation: offer.doLocation,
        pickupDate: normalizeOfferDate(offer.puDate),
        dropoffDate: normalizeOfferDate(offer.doDate),
        rentalDays: offer.rentalDays,
        specifications: offer.spec,
        terms: offer.terms,
        payment: offer.payment,
        supplier: offer.localSupplier,
        deeplink: offer.deeplink,
        availabilityId: offer.availabilityId,
      },
      customer: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone.replace(/\s/g, ''), // Strip spaces
        address: {
          street: streetAddress,
          city: formData.city,
          country: formData.country
        },
        dob: formData.dob
      }
    };
    console.log('payload', payload);
    dispatch(bookOffer(payload));
  };

  const footerButtons = [
    {
      variant: 'primary',
      onClick: handleSubmit,
      children: isSubmitting ? t('submitting') : t('booking.bookNow'),
      disabled: isSubmitting,
      loading: isSubmitting
    }
  ];

  return (
    <>
      <UnifiedModal
        show={show}
        onHide={onHide}
        title={t('booking.bookYourCar')}
        footerButtons={footerButtons}
        dir={dir}
      >
        {isSubmitting && (
          <div className="alert alert-info d-flex align-items-center mb-3" role="status" aria-live="polite">
            <div className={`spinner-border spinner-border-sm ${isRTL ? 'ml-2' : 'mr-2'}`} role="status" aria-hidden="true" />
            <span>{t('submitting')}</span>
          </div>
        )}
        {/* Offer Details */}
        <div className="offer-summary mb-4 p-3 border rounded">
          <Row>
            <Col xs={12}>
              <Image src={offer?.image} alt={offer?.model} fluid />
            </Col>
            <Col xs={12}>
              <h5>{t(acrissToCarType(offer?.group))} {t(acrissToCategory(offer?.group))} {t(offer?.spec?.transmission)} ({offer?.group})</h5>
              <p><small className="text-muted">{t('booking.youWillGetModel', { model: offer?.model })}</small></p>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs={12}>
              <h6>{t('booking.specifications')}</h6>
              <ul>
                <li>{t('booking.transmission')}: {offer?.spec?.transmission === 'A' ? t('booking.automatic') : t('booking.manual')}</li>
                <li>{t('booking.ac')}: {offer?.spec?.ac ? t('yes') : t('no')}</li>
                <li>{t('booking.seats')}: {offer?.spec?.seats}</li>
                <li>{t('booking.doors')}: {offer?.spec?.doors}</li>
                <li>{t('booking.luggage')}: {offer?.spec?.luggage}</li>
              </ul>
            </Col>
          </Row>
          {/* Pickup and Return Information */}
          <Row className="mt-3">
            <Col xs={12}>
              <div className="d-flex align-items-start mb-2">
                <FaMapMarkerAlt className={`text-primary mt-1 ${isRTL ? "ml-2" : "mr-2"}`} />
                <div>
                  <strong className="text-primary">{t('booking.pickup')}</strong>
                  <div className="text-muted small">
                    {(() => {
                      const loc = getLocationName(offer?.puLocation);
                      return loc ? (
                        <>
                          <div><strong>{loc.locationName}</strong></div>
                          <div>{loc.city}, {loc.country}</div>
                        </>
                      ) : offer?.puLocation;
                    })()}
                  </div>
                  <div className="text-muted small">
                    {parseAsLocalDateTime(offer?.puDate).toLocaleString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={12}>
              <div className="d-flex align-items-start mb-2">
                <FaMapMarkerAlt className={`text-success mt-1 ${isRTL ? "ml-2" : "mr-2"}`} />
                <div>
                  <strong className="text-success">{t('booking.return')}</strong>
                  <div className="text-muted small">
                    {(() => {
                      const loc = getLocationName(offer?.doLocation);
                      return loc ? (
                        <>
                          <div><strong>{loc.locationName}</strong></div>
                          <div>{loc.city}, {loc.country}</div>
                        </>
                      ) : offer?.doLocation;
                    })()}
                  </div>
                  <div className="text-muted small">
                    {parseAsLocalDateTime(offer?.doDate).toLocaleString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs={12}>
              <p><strong>{t('booking.supplier')}:</strong> {offer?.localSupplier?.displayName}</p>
              <p><strong>{t('deskLocation')}:</strong> {offer?.localSupplier?.deskLocation ? t(offer.localSupplier.deskLocation) : t('noDeskLocation')}</p>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs={12}>
              <h6>{t('booking.terms')}</h6>
              <ul>
                <li>{t('booking.minAge')}: {offer?.terms?.minAllowedAge}</li>
                {offer?.terms?.minDrivingExperience && <li>{t('booking.minDrivingExperience')}: {offer.terms.minDrivingExperience} {t('booking.years')}</li>}
                <li>{t('booking.kmLimit')}: {t(offer?.terms?.kmLimit)}</li>
                <li>{t('booking.excess')}: {offer?.terms?.excess} {offer?.terms?.currency}</li>
                <li>{t('booking.cdw')}: {offer?.terms?.cdw ? t('included') : "?"}</li>
                <li>{t('booking.tpl')}: {offer?.terms?.tpl ? t('included') : "?"}</li>
                {offer?.terms?.oneWayIncluded && <li>{t('oneWayIncluded')}: {t('included')}</li>}
                {offer?.terms?.tireAndGlassInsuranceIncluded && <li>{t('tireAndGlassInsuranceIncluded')}: {t('included')}</li>}
                {offer?.terms?.additionalDriverIncluded && <li>{t('additionalDriverIncluded')}: {t('included')}</li>}
                {offer?.terms?.zeroPrepayment && <li>{t('zeroPrepayment')}: {t('included')}</li>}
                <li>{t('freeCancellation')}: {t(offer?.terms?.cancellationTerms)}</li>
              </ul>
              {rentalTermsCountryCode && (
                <Button variant="link" className="p-0 small" onClick={() => setShowRentalTerms(true)}>
                  {t('booking.fullRentalTerms')}
                </Button>
              )}
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs={12}>
              <div className="border rounded bg-light p-3">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>{t('payNow')}</span>
                  <span>{formatCurrencyValue(payNowAmount, displayCurrency)}</span>
                </div>
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>{t('booking.payAtPickup')}</span>
                  <span>{formatCurrencyValue(payAtPickupAmount, displayCurrency)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-2 mt-2 border-top font-weight-bold">
                  <span>{t('booking.total')}</span>
                  <span>{formatCurrencyValue(totalAmount, displayCurrency)}</span>
                </div>
                {payNowAmount === 0 && payAtPickupAmount > 0 && (
                  <div className="mt-3 small text-muted">
                    {t('booking.priceIndicative')} {t('booking.pickupPaymentCurrency')} {t('booking.foreignExchangeFees')}
                  </div>
                )}
                {payNowAmount > 0 && payAtPickupAmount > 0 && (
                  <div className="mt-3 small text-muted">
                    {t('booking.priceIndicative')} {t('booking.prepaymentPickupCurrency')} {t('booking.foreignExchangeFees')}
                  </div>
                )}
                {payNowAmount > 0 && payAtPickupAmount === 0 && (
                  <div className="mt-3 small text-muted">
                    {t('booking.priceIndicative')} {t('booking.prepaymentCurrency')} {t('booking.foreignExchangeFees')}
                  </div>
                )}
              </div>
              <p className="mt-3 mb-0"><strong>{t('booking.rentalDays')}:</strong> {offer?.rentalDays}</p>
            </Col>
          </Row>
        </div>

        {/* Login Button if not logged in */}
        {!user && (
          <div className="text-center mb-4">
            <Button variant="primary" onClick={() => setShowLogin(true)}>
              {t('booking.loginToBook')}
            </Button>
            <p className="text-muted mt-2">{t('booking.loginPrompt')}</p>
          </div>
        )}

        {/* Customer Information Form - only show if logged in */}
        {user && (
          <Form noValidate autoComplete="on">
          <div className="booking-form-section">
            <h6 className="section-title mb-3">
              <FaUser className={isRTL ? "ml-2" : "mr-2"} />
              {t('booking.customerInfo')}
            </h6>

          <Row>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label className="required-field" htmlFor="firstName">
                  {t('booking.firstName')}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    onInput={handleChange}
                    onBlur={handleBlur}
                    placeholder={t('booking.firstNamePlaceholder')}
                    isInvalid={fieldTouched.firstName && !fieldValid.firstName}
                    isValid={fieldTouched.firstName && fieldValid.firstName && formData.firstName}
                    autoComplete="given-name"
                    aria-describedby="firstNameHelp"
                  />

                </InputGroup>
                {fieldTouched.firstName && !fieldValid.firstName && errors.firstName && (
                  <div className="text-danger small mt-1">{typeof errors.firstName === 'string' ? t(`booking.${errors.firstName}`) : t(`booking.${errors.firstName.key}`, errors.firstName.params)}</div>
                )}
                <Form.Text id="firstNameHelp" muted>
                  {t('booking.firstNameHelp')}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label className="required-field" htmlFor="lastName">
                  {t('booking.lastName')}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    onInput={handleChange}
                    onBlur={handleBlur}
                    placeholder={t('booking.lastNamePlaceholder')}
                    isInvalid={fieldTouched.lastName && !fieldValid.lastName}
                    isValid={fieldTouched.lastName && fieldValid.lastName && formData.lastName}
                    autoComplete="family-name"
                    aria-describedby="lastNameHelp"
                  />

                </InputGroup>
                {fieldTouched.lastName && !fieldValid.lastName && errors.lastName && (
                  <div className="text-danger small mt-1">{typeof errors.lastName === 'string' ? t(`booking.${errors.lastName}`) : t(`booking.${errors.lastName.key}`, errors.lastName.params)}</div>
                )}
                <Form.Text id="lastNameHelp" muted>
                  {t('booking.lastNameHelp')}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="alert alert-warning mb-3">
            <strong>{t('booking.driverNameNote')}</strong>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="required-field" htmlFor="email">
              <FaEnvelope className={isRTL ? "ml-2" : "mr-2"} />
              {t('booking.email')}
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onInput={handleChange}
                onBlur={handleBlur}
                placeholder={t('booking.emailPlaceholder')}
                readOnly={!!user}
                isInvalid={fieldTouched.email && !fieldValid.email}
                isValid={fieldTouched.email && fieldValid.email && formData.email}
                autoComplete="email"
                aria-describedby="emailHelp"
              />

            </InputGroup>
            {fieldTouched.email && !fieldValid.email && errors.email && (
              <div className="text-danger small mt-1">{typeof errors.email === 'string' ? t(`booking.${errors.email}`) : t(`booking.${errors.email.key}`, errors.email.params)}</div>
            )}
            <Form.Text id="emailHelp" muted>
              {t('booking.emailLocked')}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="required-field" htmlFor="phone">
              <FaPhone className={isRTL ? "ml-2" : "mr-2"} />
              {t('booking.phone')}
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onInput={handleChange}
                onBlur={handleBlur}
                placeholder={t('booking.phonePlaceholder')}
                isInvalid={fieldTouched.phone && !fieldValid.phone}
                isValid={fieldTouched.phone && fieldValid.phone && formData.phone}
                autoComplete="tel"
                aria-describedby="phoneHelp"
              />

            </InputGroup>
            {fieldTouched.phone && !fieldValid.phone && errors.phone && (
              <div className="text-danger small mt-1">{typeof errors.phone === 'string' ? t(`booking.${errors.phone}`) : t(`booking.${errors.phone.key}`, errors.phone.params)}</div>
            )}
            <Form.Text id="phoneHelp" muted>
              {t('booking.phoneHelp')}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="required-field" htmlFor="dob">
              <FaCalendarAlt className={isRTL ? "ml-2" : "mr-2"} />
              {t('booking.dob')}
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="date"
                id="dob"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                onInput={handleChange}
                onBlur={handleBlur}
                isInvalid={fieldTouched.dob && !fieldValid.dob}
                isValid={fieldTouched.dob && fieldValid.dob && formData.dob}
                max={new Date().toISOString().split('T')[0]}
                aria-describedby="dobHelp"
              />

            </InputGroup>
            {fieldTouched.dob && !fieldValid.dob && errors.dob && (
              <div className="text-danger small mt-1">{typeof errors.dob === 'string' ? t(`booking.${errors.dob}`) : t(`booking.${errors.dob.key}`, errors.dob.params)}</div>
            )}
            <Form.Text id="dobHelp" muted>
              {t('booking.dobHelp')}
            </Form.Text>
          </Form.Group>
        </div>

        {/* Address Information */}
        <div className="booking-form-section">
          <h6 className="section-title mb-3">
            <FaMapMarkerAlt className={isRTL ? "ml-2" : "mr-2"} />
            {t('booking.residenceInfo')}
          </h6>

          <Form.Group className="mb-3">
            <Form.Label className="required-field" htmlFor="streetAddress">
              {t('booking.streetAddress')}
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                id="streetAddress"
                name="streetAddress"
                value={formData.streetAddress}
                onChange={handleChange}
                onInput={handleChange}
                onBlur={handleBlur}
                placeholder={t('booking.streetAddressPlaceholder')}
                isInvalid={fieldTouched.streetAddress && !fieldValid.streetAddress}
                isValid={fieldTouched.streetAddress && fieldValid.streetAddress && formData.streetAddress}
                autoComplete="address-line1"
              />

            </InputGroup>
            {fieldTouched.streetAddress && !fieldValid.streetAddress && errors.streetAddress && (
              <div className="text-danger small mt-1">{typeof errors.streetAddress === 'string' ? t(`booking.${errors.streetAddress}`) : t(`booking.${errors.streetAddress.key}`, errors.streetAddress.params)}</div>
            )}
          </Form.Group>

          <Row>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label className="required-field" htmlFor="city">
                  {t('booking.city')}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    onInput={handleChange}
                    onBlur={handleBlur}
                    placeholder={t('booking.cityPlaceholder')}
                    isInvalid={fieldTouched.city && !fieldValid.city}
                    isValid={fieldTouched.city && fieldValid.city && formData.city}
                    autoComplete="address-level2"
                  />

                </InputGroup>
                {fieldTouched.city && !fieldValid.city && errors.city && (
                  <div className="text-danger small mt-1">{typeof errors.city === 'string' ? t(`booking.${errors.city}`) : t(`booking.${errors.city.key}`, errors.city.params)}</div>
                )}
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label className="required-field" htmlFor="country">
                  {t('booking.country')}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    as="select"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={fieldTouched.country && !fieldValid.country}
                    isValid={fieldTouched.country && fieldValid.country && formData.country}
                    autoComplete="country"
                  >
                    <option value="">{t('booking.selectCountry')}</option>
                    {countries.map(country => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </Form.Control>

                </InputGroup>
                {fieldTouched.country && !fieldValid.country && errors.country && (
                  <div className="text-danger small mt-1">{typeof errors.country === 'string' ? t(`booking.${errors.country}`) : t(`booking.${errors.country.key}`, errors.country.params)}</div>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Airport Information */}
        {isAirport && (
          <div className="booking-form-section">
            <h6 className="section-title mb-3">
              <FaPlane className={isRTL ? "ml-2" : "mr-2"} />
              {t('booking.flightInfo')}
            </h6>

            <Row>
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="flightNumber">
                    <FaPlane className={isRTL ? "ml-2" : "mr-2"} />
                    {t('booking.flightNumber')}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id="flightNumber"
                    name="flightNumber"
                    value={formData.flightNumber}
                    onChange={handleChange}
                    onInput={handleChange}
                    onBlur={handleBlur}
                    placeholder={t('booking.flightNumberPlaceholder')}
                    autoComplete="off"
                  />
                  <Form.Text muted>
                    {t('booking.flightNumberHelp')}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="landingTime">
                    {t('booking.landingTime')}
                  </Form.Label>
                  <Form.Control
                    as="select"
                    id="landingTime"
                    name="landingTime"
                    value={formData.landingTime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">{t('booking.selectTime')}</option>
                    {timeOptions.map(time => (
                      <option key={time.value} value={time.value}>
                        {time.label}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Text muted>
                    {t('booking.landingTimeHelp')}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>
        )}

        {/* Additional Notes */}
        <div className="booking-form-section">
          <h6 className="section-title mb-3">
            <FaStickyNote className={isRTL ? "ml-2" : "mr-2"} />
            {t('booking.additionalNotes')}
          </h6>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="notes">
              {t('booking.notes')}
            </Form.Label>
            <Form.Control
              as="textarea"
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              onInput={handleChange}
              onBlur={handleBlur}
              placeholder={t('booking.notesPlaceholder')}
              maxLength={300}
              rows={3}
              isInvalid={fieldTouched.notes && !fieldValid.notes}
              isValid={fieldTouched.notes && fieldValid.notes && formData.notes}
              aria-describedby="notesHelp"
            />
            <div className="d-flex justify-content-between">
              {fieldTouched.notes && !fieldValid.notes && errors.notes && (
                <div className="text-danger small">{typeof errors.notes === 'string' ? t(`booking.${errors.notes}`) : t(`booking.${errors.notes.key}`, errors.notes.params)}</div>
              )}
              <Badge
                bg={formData.notes.length > 250 ? 'warning' : 'secondary'}
                className={isRTL ? "mr-auto" : "ml-auto"}
              >
                {formData.notes.length}/300
              </Badge>
            </div>
            <Form.Text id="notesHelp" muted>
              {t('booking.notesHelp')}
            </Form.Text>
          </Form.Group>
        </div>

        {/* Credit Card Information */}
        <div className="booking-form-section">
          <h6 className="section-title mb-3">
            <FaCreditCard className={isRTL ? "ml-2" : "mr-2"} />
            {t('booking.creditCardInfo')}
          </h6>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="cardHolderName">
              {t('booking.cardHolderName')}
            </Form.Label>
            <Form.Control
              type="text"
              id="cardHolderName"
              name="cardHolderName"
              value={`${formData.firstName} ${formData.lastName}`.trim()}
              readOnly
              autoComplete="cc-name"
              isValid={fieldTouched.firstName && fieldValid.firstName && fieldTouched.lastName && fieldValid.lastName}
            />
            <Form.Text muted>
              {t('booking.cardHolderNote')}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="required-field" htmlFor="cardNumber">
              {t('booking.cardNumber')}
            </Form.Label>
            <Form.Control
              type="text"
              id="cardNumber"
              name="cardNumber"
              value={formData.cardNumber}
              onChange={handleChange}
              onInput={handleChange}
              onBlur={handleBlur}
              placeholder="1234 5678 9012 3456"
              maxLength={19}
              isInvalid={fieldTouched.cardNumber && !fieldValid.cardNumber}
              isValid={fieldTouched.cardNumber && fieldValid.cardNumber && formData.cardNumber}
              autoComplete="cc-number"
              inputMode="numeric"
              dir="ltr"
              aria-describedby="cardNumberHelp"
            />
            {fieldTouched.cardNumber && !fieldValid.cardNumber && errors.cardNumber && (
              <div className="text-danger small mt-1">{typeof errors.cardNumber === 'string' ? t(`booking.${errors.cardNumber}`) : t(`booking.${errors.cardNumber.key}`, errors.cardNumber.params)}</div>
            )}
            <Form.Text id="cardNumberHelp" muted>
              {t('booking.cardNumberHelp')}
            </Form.Text>
          </Form.Group>

          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="required-field" htmlFor="expiryDate">
                  {t('booking.expiryDate')}
                </Form.Label>
                <Form.Control
                  type="text"
                  id="expiryDate"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleChange}
                  onInput={handleChange}
                  onBlur={handleBlur}
                  placeholder="MM/YY"
                  maxLength={5}
                  isInvalid={fieldTouched.expiryDate && !fieldValid.expiryDate}
                  isValid={fieldTouched.expiryDate && fieldValid.expiryDate && formData.expiryDate}
                  autoComplete="cc-exp"
                  inputMode="numeric"
                  dir="ltr"
                  aria-describedby="expiryDateHelp"
                />
                {fieldTouched.expiryDate && !fieldValid.expiryDate && errors.expiryDate && (
                  <div className="text-danger small mt-1">{typeof errors.expiryDate === 'string' ? t(`booking.${errors.expiryDate}`) : t(`booking.${errors.expiryDate.key}`, errors.expiryDate.params)}</div>
                )}
                <Form.Text id="expiryDateHelp" muted>
                  {t('booking.expiryDateHelp')}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="required-field" htmlFor="cvv">
                  {t('booking.cvv')}
                </Form.Label>
                <Form.Control
                  type="text"
                  id="cvv"
                  name="cvv"
                  value={formData.cvv}
                  onChange={handleChange}
                  onInput={handleChange}
                  onBlur={handleBlur}
                  placeholder="123"
                  maxLength={4}
                  isInvalid={fieldTouched.cvv && !fieldValid.cvv}
                  isValid={fieldTouched.cvv && fieldValid.cvv && formData.cvv}
                  autoComplete="cc-csc"
                  inputMode="numeric"
                  dir="ltr"
                  aria-describedby="cvvHelp"
                />
                {fieldTouched.cvv && !fieldValid.cvv && errors.cvv && (
                  <div className="text-danger small mt-1">{typeof errors.cvv === 'string' ? t(`booking.${errors.cvv}`) : t(`booking.${errors.cvv.key}`, errors.cvv.params)}</div>
                )}
                <Form.Text id="cvvHelp" muted>
                  {t('booking.cvvHelp')}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center pt-2 mt-2 border-top font-weight-bold">
            <span>{t('payNowNote')}</span>
            <strong>{formatCurrencyValue(payNowAmount, displayCurrency)}</strong>
          </div>

          {payNowAmount > 0 && payAtPickupAmount > 0 && (
            <div className="mt-2 small text-muted">
              {t('booking.priceIndicative')} {t('booking.prepaymentPickupCurrency')} {t('booking.foreignExchangeFees')}
            </div>
          )}
          {payNowAmount > 0 && payAtPickupAmount === 0 && (
            <div className="mt-2 small text-muted">
              {t('booking.priceIndicative')} {t('booking.prepaymentCurrency')} {t('booking.foreignExchangeFees')}
            </div>
          )}
        </div>

        </Form>
        )}

        {validationFailed && (
          <div className="validation-error-indicator alert alert-danger d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FaExclamationTriangle className={isRTL ? "ml-2" : "mr-2"} />
              <span>{t('booking.pleaseFixErrors')}</span>
            </div>
            <Button 
              variant="outline-danger" 
              size="sm" 
              onClick={() => {
                const firstInvalidInput = document.querySelector('.form-control.is-invalid');
                if (firstInvalidInput) {
                  firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  firstInvalidInput.focus();
                } else {
                  document.querySelector('.booking-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              {t('booking.scrollToErrors')}
            </Button>
          </div>
        )}

        {errors.submit && (
          <div className="alert alert-danger mt-3">
            <FaExclamationTriangle className={isRTL ? "ml-2" : "mr-2"} />
            {t(`booking.${errors.submit.key}`, errors.submit.params)}
          </div>
        )}
      </UnifiedModal>
      <LoginModal shouldShow={showLogin} afterLogin={afterLogin} setModalVisible={() => setShowLogin(false)} dir={dir} user={user} cid={cid} />
      <RentalTermsModal show={showRentalTerms} onHide={() => setShowRentalTerms(false)} countryCode={rentalTermsCountryCode} dir={dir} />
    </>
  );
};

export default BookingModal;